import { SearchTreeNode } from '@/types';
import { walkTreeAndFindNodeByEphemeralId } from '@/util';
import { findPlaceholdersInSearchTree } from '@/pages/SearchTemplatePage/util';

export type ShouldConfirmDeleteResult =
  | {
      shouldConfirm: true;
      kind: 'rule' | 'group';
    }
  | {
      shouldConfirm: false;
    };

/**
 * Determines whether deleting a node (rule or group) requires confirmation based on
 * whether the node or its subtree contains placeholder-backed filters.
 *
 * @param searchTree - The search tree to search within
 * @param nodeId - Ephemeral id (_leafNodeId or _internalNodeId) of the target node
 * @returns Object with shouldConfirm, title, and description for the dialog. When
 *   shouldConfirm is false, title/description are defaults (fail-open).
 */
export function shouldConfirmDeleteForNode(
  searchTree: SearchTreeNode | undefined,
  nodeId: string | undefined
): ShouldConfirmDeleteResult {
  const defaultNoConfirm: ShouldConfirmDeleteResult = {
    shouldConfirm: false,
  };

  if (!searchTree || !nodeId) {
    return defaultNoConfirm;
  }

  const node = walkTreeAndFindNodeByEphemeralId(searchTree, nodeId);
  if (!node) {
    return defaultNoConfirm;
  }

  const placeholders = findPlaceholdersInSearchTree(node);
  const hasPlaceholders = Object.keys(placeholders).length > 0;

  if (!hasPlaceholders) {
    return defaultNoConfirm;
  }

  return {
    shouldConfirm: true,
    kind: node.op === 'filter' ? 'rule' : 'group',
  };
}

/**
 * Find the node id of the rule or group that an element is within. This is found
 * in the data-id attribute of the closest ancestor with the class group-or-rule
 */

export function getNodeIdOfDeleteButton(element: Element): string | undefined {
  const ancestor = element.closest('.group-or-rule');
  return ancestor?.getAttribute('data-id') ?? undefined;
}
